import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as storyEntitiesService from '../supabase/story-entities';
import type { StoryEntity, StoryEntityWithDetails } from '../supabase/story-entities';

// Define cache times - using the same aggressive caching as other queries
const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;
const STALE_TIME = DAY_IN_MS * 30; // 30 days stale time
const GC_TIME = DAY_IN_MS * 60; // 60 days garbage collection

// Define query keys for better cache management
export const storyEntityKeys = {
  all: ['storyEntities'] as const,
  byStory: (storyId: string) => 
    [...storyEntityKeys.all, 'byStory', storyId] as const,
};

/**
 * Hook to fetch entities for a story
 * @param storyId The story ID to fetch entities for
 * @param options Additional options for the query
 */
export function useStoryEntities(
  storyId: string | undefined,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: storyEntityKeys.byStory(storyId || ''),
    queryFn: async () => {
      if (!storyId) return [];
      return storyEntitiesService.getStoryEntities(storyId);
    },
    enabled: !!storyId && (options.enabled !== false),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to add an entity to a story
 */
export function useAddEntityToStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      storyId,
      entityId,
      role = ''
    }: {
      storyId: string;
      entityId: number;
      role?: string;
    }) => {
      return storyEntitiesService.addEntityToStory(storyId, entityId, role);
    },
    onSuccess: (_, variables) => {
      // Invalidate the story entities query
      queryClient.invalidateQueries({
        queryKey: storyEntityKeys.byStory(variables.storyId),
      });
    },
  });
}

/**
 * Hook to remove an entity from a story
 */
export function useRemoveEntityFromStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      storyId,
      entityId
    }: {
      storyId: string;
      entityId: number;
    }) => {
      await storyEntitiesService.removeEntityFromStory(storyId, entityId);
      return { storyId, entityId };
    },
    onSuccess: ({ storyId }) => {
      // Invalidate the story entities query
      queryClient.invalidateQueries({
        queryKey: storyEntityKeys.byStory(storyId),
      });
    },
  });
}

/**
 * Hook to update an entity's role in a story
 */
export function useUpdateEntityRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      storyId,
      entityId,
      role
    }: {
      storyId: string;
      entityId: number;
      role: string;
    }) => {
      return storyEntitiesService.updateEntityRole(storyId, entityId, role);
    },
    onSuccess: (_, variables) => {
      // Invalidate the story entities query
      queryClient.invalidateQueries({
        queryKey: storyEntityKeys.byStory(variables.storyId),
      });
    },
  });
}
